import { LogoMark, pageContext, ReportProblemDialog, Wordmark } from "@flexfreight/ui";
import { api } from "../api/client";
import { useEffect, useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useConsoleMe } from "../auth/context";
import { SESSION_KEY } from "../auth/ConsoleGate";
import { signOut } from "../auth/session";
import { CommandPalette, type PaletteItem } from "./CommandPalette";
import { NotificationBell } from "./Notifications";
import { consoleTitle, navFor, navGroupFor } from "./nav";

const ROLE_LABEL: Record<string, string> = {
  owner: "Owner",
  admin: "Admin",
  ops: "Operations",
  sales: "Sales",
  marketing: "Marketing",
  read_only: "Read only",
};

/** Up to two initials for the account chip ("Demo Ops" -> "DO"). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? `${parts[0]![0]}${parts[parts.length - 1]![0]}` : (parts[0] ?? "").slice(0, 2);
  return letters.toUpperCase() || "?";
}

/** Arrow keys walk the sidebar (group labels and links alike); Home/End jump. Enter/Space on a label is native. */
function navKeys(e: ReactKeyboardEvent<HTMLElement>) {
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
  const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>(".ffa-group__head, .ffa-nav__link")).filter((el) => !el.closest("[hidden]"));
  const i = items.indexOf(document.activeElement as HTMLElement);
  if (i < 0) return;
  e.preventDefault();
  const next = e.key === "Home" ? 0 : e.key === "End" ? items.length - 1 : e.key === "ArrowDown" ? Math.min(items.length - 1, i + 1) : Math.max(0, i - 1);
  items[next]?.focus();
}

/**
 * The console's one application shell: accordion sidebar (every group collapsed by default, only one open at a time;
 * the current route's group opens on load and on every navigation), top bar, and the routed
 * page. On phones the sidebar becomes a drawer behind the menu button.
 */
export function ConsoleShell() {
  const { pathname } = useLocation();
  const me = useConsoleMe();
  const qc = useQueryClient();
  const groups = navFor(me.capabilities);
  // Accordion: at most one group open. Starts on the group holding the current page (none off the nav).
  const [openGroup, setOpenGroup] = useState<string | null>(() => navGroupFor(pathname)?.id ?? null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const pages = useMemo<PaletteItem[]>(() => groups.flatMap((g) => g.items.map((i) => ({ id: `p:${i.to}`, label: i.label, hint: g.label, to: i.to, group: "Pages" }))), [groups]);
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

  // Cmd/Ctrl+K toggles search anywhere; "/" opens it when not typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      const t = e.target as HTMLElement | null;
      const typing = t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
      if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.title = consoleTitle(pathname);
    setDrawerOpen(false);
    // The group holding the page just opened is the one open group.
    const active = navGroupFor(pathname)?.id;
    if (active) setOpenGroup(active);
  }, [pathname]);

  function toggleGroup(id: string) {
    setOpenGroup((prev) => (prev === id ? null : id));
  }

  return (
    <div className="ffa-shell" data-drawer={drawerOpen ? "open" : "closed"}>
      <a className="ffa-skip" href="#ffa-main">
        Skip to content
      </a>
      <aside className="ffa-side ff-dark-scope" aria-label="Console navigation">
        <div className="ffa-brand">
          <LogoMark className="ffa-brand__mark" title="" />
          <Wordmark className="ffa-brand__word" />
          <span className="ffa-brand__tag">Admin</span>
        </div>
        <nav className="ffa-nav" onKeyDown={navKeys}>
          {groups.map((group) => {
            const open = openGroup === group.id;
            const listId = `ffa-group-${group.id}`;
            return (
              <div className="ffa-group" key={group.id}>
                <button
                  type="button"
                  className="ffa-group__head"
                  aria-expanded={open}
                  aria-controls={listId}
                  onClick={() => toggleGroup(group.id)}
                >
                  <span>{group.label}</span>
                  <svg className="ffa-group__chev" viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
                <ul id={listId} className="ffa-group__list" hidden={!open}>
                  {group.items.map((item) => (
                    <li key={item.to}>
                      <NavLink to={item.to} end={item.end} className="ffa-nav__link">
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
        <button type="button" className="ffa-report" onClick={() => setReporting(true)}>
          Report a problem
        </button>
      </aside>
      {drawerOpen ? <button type="button" className="ffa-scrim" aria-label="Close navigation" onClick={() => setDrawerOpen(false)} /> : <span className="ffa-scrim" aria-hidden="true" />}
      <div className="ffa-body">
        <header className="ffa-top">
          <button
            type="button"
            className="ffa-menu"
            aria-label="Open navigation"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
          >
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
          <span className="ffa-top__brand" aria-hidden="true">
            <LogoMark className="ffa-brand__mark" title="" />
            <span className="ffa-brand__tag">Admin</span>
          </span>
          <span className="ffa-top__area">Admin console</span>
          <button type="button" className="ffa-search-trigger" onClick={() => setPaletteOpen(true)} aria-keyshortcuts={mac ? "Meta+K" : "Control+K"}>
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <circle cx="9" cy="9" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
              <path d="M13.2 13.2 17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <span className="ffa-search-trigger__text">Search</span>
            <span className="ffa-search-trigger__keys" aria-hidden="true"><kbd>{mac ? "⌘" : "Ctrl"}</kbd><kbd>K</kbd></span>
          </button>
          {me.carriers && me.carriers !== "unknown" ? (
            <span className="ffa-env" data-env={me.carriers} title="Where carrier calls go. Change it per carrier in Carriers & API keys.">
              {{ mock: "Carriers: mock", test: "Carriers: test systems", live: "Carriers: live", mixed: "Carriers: some live" }[me.carriers]}
            </span>
          ) : null}
          <NotificationBell />
          {me.capabilities.includes("quotes:create") ? (
            <Link to="/quotes/new" className="ffx-btn ffx-btn--primary ffa-top__cta">
              New quote
            </Link>
          ) : null}
          <span className="ffa-who" title={me.user.email}>
            <span className="ff-monogram ffa-who__avatar" aria-hidden="true">
              {initials(me.user.name)}
            </span>
            <span className="ffa-who__text">
              <span className="ffa-who__name">{me.user.name}</span>
              <span className="ffa-who__role">{ROLE_LABEL[me.role] ?? me.role}</span>
            </span>
          </span>
          <button type="button" className="ffa-signout" onClick={() => void signOut().finally(() => qc.invalidateQueries({ queryKey: SESSION_KEY }))}>
            Sign out
          </button>
        </header>
        <main id="ffa-main" className="ffa-main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      <ReportProblemDialog open={reporting} onClose={() => setReporting(false)} submit={(description) => api("/v1/me/bug-reports", { method: "POST", body: { source: "console", description, ...pageContext() } })} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} pages={pages} />
    </div>
  );
}
